import React from "react";
import config from "../config.js";

// Forecast has no predicted water LEVEL, so the verdict comes from the flow
// bucket (config.js is the source of truth upstream) crossed with clarity.
// Solid accents (no gradients/stripes) keep the section calm.
const DOT = {
	dry: "#9ca3af",
	low: "#d4a017", // clear but thin
	swim: "#22c55e",
	prime: "#16a34a",
	clearing: "#a9784f", // light brown: on its way back
	murky: "#6b4423", // dark brown: still dirty
	flood: "#dc2626",
	unknown: "#9ca3af",
};

// A swim endorsement takes BOTH a good flow and clear water, so green never
// appears on water you wouldn't get into. Anything short of clear keeps the
// plain flow word and lets the dot carry how dirty it is.
const CLEAR_WORD = { lowFlow: "Low", medFlow: "Swimmable", hiFlow: "Prime" };
const CLEAR_DOT = { lowFlow: DOT.low, medFlow: DOT.swim, hiFlow: DOT.prime };
const FLOW_WORD = { lowFlow: "Low", medFlow: "Medium", hiFlow: "High" };
const DIRTY_DOT = { murky: DOT.murky, clearing: DOT.clearing };

// Flood outranks clarity: the red danger call stands on its own, and every
// flood is muddy anyway. Dry cells make no clarity claim (see below), so they
// stay grey whatever the curve says.
function category(flowKey, clarity) {
	if (flowKey === "extFlow") return { word: "Flood", color: DOT.flood };
	if (!FLOW_WORD[flowKey]) return { word: "Dry", color: DOT.dry };
	if (clarity === "clear") {
		return { word: CLEAR_WORD[flowKey], color: CLEAR_DOT[flowKey] };
	}
	// murky, clearing, or clarity we don't have — none of them earn green
	return {
		word: FLOW_WORD[flowKey],
		color: DIRTY_DOT[clarity] || DOT.unknown,
	};
}

// The projected flow number is the hero of this section: one decimal under
// 10 cfs (where tenths matter for a creek), whole numbers above.
function fmtFlow(v) {
	if (v == null || Number.isNaN(v)) return "—";
	if (v < 10) return v.toFixed(1);
	return Math.round(v).toLocaleString("en-US");
}

// Label for a horizon's valid time, in Austin local time. The nearest
// forecasts read relative ("Today"/"Tomorrow" — which flips at midnight
// Central, and after the afternoon model run the first cell is tomorrow's);
// farther days get their weekday and date. "at 3 PM" renders as a sub-line.
function dayLabel(validUtc) {
	const chicagoDate = (d) =>
		d.toLocaleDateString("en-US", { timeZone: "America/Chicago" });
	const d = new Date(validUtc);
	const now = new Date();
	if (chicagoDate(d) === chicagoDate(now)) return "Today";
	if (chicagoDate(d) === chicagoDate(new Date(now.getTime() + 864e5))) {
		return "Tomorrow";
	}
	const weekday = new Intl.DateTimeFormat("en-US", {
		weekday: "short",
		timeZone: "America/Chicago",
	}).format(d);
	const dayNum = new Intl.DateTimeFormat("en-US", {
		day: "numeric",
		timeZone: "America/Chicago",
	}).format(d);
	return `${weekday} ${dayNum}`;
}

// Same flow-bucket lookup the stat blocks use (config.flowMaxes upper bounds).
function flowKeyFor(flow) {
	for (const [key, max] of Object.entries(config.flowMaxes)) {
		if (flow < max) return key;
	}
	return "extFlow";
}

// Rain expected in the 24h before the forecast time, shown in inches. Any
// measurable amount (>= 0.01 in, the standard reporting minimum) is shown,
// so this never says "no rain" when a weather forecast shows some.
function rainLabel(rainMm) {
	if (rainMm == null) return null;
	const inches = rainMm / 25.4;
	if (inches < 0.01) return { text: "no rain", wet: false };
	const amt = inches < 1 ? inches.toFixed(2) : inches.toFixed(1);
	return { text: `${amt} in rain`, wet: true };
}

// Water-clarity badge (model clarity.py): a qualitative estimate of
// post-storm murkiness from the hydrograph — never a number.
const CLARITY = {
	murky: { text: "murky", cls: "murky" },
	clearing: { text: "cloudy", cls: "clearing" },
	clear: { text: "clear", cls: "clear" },
};

// A single day cell: date, big flow number, expected range, category tag.
function ForecastCell({ label, flow, p10, p90, flowKey, confidence, rainMm, clarity, isNow }) {
	const cat = category(flowKey, clarity);
	const tip =
		confidence == null
			? undefined
			: `${Math.round(confidence * 100)}% confidence in this category`;
	return (
		<div className={`wis-cell${isNow ? " wis-cell-now" : ""}`} title={tip}>
			<div className="wis-cell-day">{label}</div>
			<div className="wis-cell-day-sub">{isNow ? "" : "at 3 PM"}</div>
			<div className="wis-cell-flow">
				{fmtFlow(flow)}
				<span className="wis-cell-unit">
					ft<sup>3</sup>/s
				</span>
			</div>
			<div className="wis-cell-range">
				{isNow ? "measured" : `${fmtFlow(p10)}–${fmtFlow(p90)}`}
			</div>
			<div className="wis-cell-cat">
				<span className="wis-cat-dot" style={{ background: cat.color }} />
				{cat.word}
			</div>
			{rainLabel(rainMm) && (
				<div className={`wis-cell-rain${rainLabel(rainMm).wet ? " wet" : ""}`}>
					{rainLabel(rainMm).wet ? "\u{1F4A7} " : ""}{rainLabel(rainMm).text}
				</div>
			)}
			{/* no clarity claim on Dry cells: near-zero flow means stagnant
			    pools, and the storm-decay curve says nothing about those */}
			{flowKey !== "negFlow" && CLARITY[clarity] && (
				<div className={`wis-cell-clarity ${CLARITY[clarity].cls}`}>
					{CLARITY[clarity].text}
				</div>
			)}
		</div>
	);
}

// Factual one-line summary: where the number is headed over the window.
// The day count comes from the last cell's horizon so it stays honest when
// the window changes or the expired first cell is dropped.
function trendSummary(now, horizons) {
	const last = horizons[horizons.length - 1];
	if (now == null || !last) return "";
	let word = "Holding steady";
	if (last.flow < now * 0.8) word = "Trending down";
	else if (last.flow > now * 1.25) word = "Trending up";
	const days = Math.round(last.h / 24);
	return `${word} · ${fmtFlow(now)} → ${fmtFlow(last.flow)} ft³/s over ${days} days`;
}

// One gauge: name + trend + a Now cell followed by the 5 daily cells.
// liveFlow is the browser-fetched USGS reading; the forecast.json snapshot
// (gauge.now, taken at the daily model run) is only a fallback.
function ForecastRow({ gauge, name, liveFlow }) {
	if (!gauge || !gauge.horizons) return null;
	const nowFlow = liveFlow != null ? liveFlow : gauge.now.flow;
	const nowKey = liveFlow != null ? flowKeyFor(liveFlow) : gauge.now.flowKey;
	// A cell whose valid time has passed is history, not a forecast: between
	// 3 PM and the ~15:45 CT model refresh, yesterday's first horizon points
	// at a time that already happened. Drop expired cells so the row flips to
	// Tomorrow right at 3 PM instead of waiting on the new run; the Now cell
	// covers the present.
	const horizons = gauge.horizons.filter(
		(h) => new Date(h.valid_utc).getTime() > Date.now()
	);
	return (
		<div className="wis-row">
			<div className="wis-row-head">
				<div className="wis-row-name">{name}</div>
				<div className="wis-row-summary">
					{trendSummary(nowFlow, horizons)}
				</div>
			</div>
			<div className="wis-cells">
				<ForecastCell
					label="Now"
					flow={nowFlow}
					flowKey={nowKey}
					rainMm={gauge.now.rain_mm}
					clarity={gauge.now.clarity}
					isNow
				/>
				{horizons.map((h) => (
					<ForecastCell
						key={h.h}
						label={dayLabel(h.valid_utc)}
						flow={h.flow}
						p10={h.p10}
						p90={h.p90}
						flowKey={h.flowKey}
						confidence={h.confidence}
						rainMm={h.rain_mm}
						clarity={h.clarity}
					/>
				))}
			</div>
		</div>
	);
}

export default ForecastRow;
